export interface DescriptionOptionField {
  id: string;
  name: string;
  isEnabled: boolean;
  index: string;
  group?: boolean;
  groupText?: string;
}

export interface PricingSimpleItem {
  id: string;
  newPleatId?: any;
  name: string;
  nameSubText?: string;
  price: string;
  image?: string;
  isActive?: boolean;
  isDeleted?: boolean;
}

export interface PricingGroupItem {
  group: {
    name: string;
    nameSubText?: string;
    items: PricingSimpleItem[];
  };
}

export type PricingGroupOrSimpleItem = PricingGroupItem | PricingSimpleItem;

export interface BundleSelections {
  mainFabric: boolean;
  linings: boolean;
  embellishments: boolean;
  hardware: boolean;
  headrail: boolean;
  fees: boolean;
  shippingAndHandling: boolean;
}

export interface DraperyItemDetailsFields {
  id: number;
  pricingTypeId: number;
  supplierId?: number | null;
  pricingTypeName: string;
  isClientPricingActionOn: boolean;
  clientPricingAction: string;
  clientPricingValue: number | '';
  isPartialWidthRoundingOn: boolean;
  partialWidthRoundUpTypeId: number;
  partialWidthRoundUpTypeName: string;
  pleatTypesWithPricing: PricingSimpleItem[];
  liningAndInterliningWithPricing: PricingSimpleItem[];
  valanceAndReturns: PricingSimpleItem[] | [];
  cafeCurtainsWidthInches: string;
  cafeCurtainsWidthPricing: string;
  extraLargePanelsLengthInches: string;
  extraLargePanelsLengthPricing: string;
  extraLargePanelsWidthInches: string;
  extraLargePanelsWidthPricing: string;
  draperyPinningWidthPricing: string;
  specialityFabricsMarkUpPercentage: string;
  embellishmentsWithPricing: PricingGroupOrSimpleItem[];
  addOnsWithPricing: PricingSimpleItem[];
  salesDescriptionSettings: DescriptionOptionField[];
  orderingDescriptionSettings: DescriptionOptionField[];
  patternMatchingUpchargesSelected?: boolean;
  patternMatchingUpcharges: PricingSimpleItem[];
  isBundlingSelected: boolean;
  bundleSelections: BundleSelections;
}

export type DraperyItemDetailsFieldsPartial = Partial<DraperyItemDetailsFields>;

export type DraperyItemDetailsFieldsAction =
  | {
      type: 'updateFormFields';
      formFieldUpdates: DraperyItemDetailsFieldsPartial;
    }
  | { type: 'resetFormFields' };

const pleatTypesWithPricingInitialState: PricingSimpleItem[] = [
  {
    id: 'noneflat',
    name: 'None (flat)',
    price: '',
  },
  {
    id: 'euro2finger',
    name: 'Euro (2 Finger)',
    price: '',
  },
  {
    id: 'euro3finger',
    name: 'Euro (3 Finger)',
    price: '',
  },
  {
    id: 'goblet',
    name: 'Goblet',
    price: '',
  },
  {
    id: 'inverted',
    name: 'Inverted',
    price: '',
  },
  {
    id: 'pinch2finger',
    name: 'Pinch (2 Finger)',
    price: '',
  },
  {
    id: 'pinch3finger',
    name: 'Pinch (3 Finger)',
    price: '',
  },
];

const liningAndInterliningWithPricingInitialState: PricingSimpleItem[] = [
  {
    id: '1stlayerlining',
    name: '1st Layer (Lining)',
    price: '',
  },
  {
    id: '2ndlayerinterlining',
    name: '2nd Layer (Interlining)',
    price: '',
  },
  {
    id: '3rdlayerandfollowing',
    name: '3rd Layer and Following',
    price: '',
  },
];

const addOnsWithPricingInitialState: PricingSimpleItem[] = [
  {
    id: 'beadedChain',
    name: 'Weight: Beaded Chain',
    price: '',
  },
  {
    id: 'standardCorner',
    name: 'Weight: Standard (Corner)',
    price: '',
  },
];

export const bundleSelectionsInitialState: BundleSelections = {
  mainFabric: false,
  linings: false,
  embellishments: false,
  hardware: false,
  headrail: false,
  fees: false,
  shippingAndHandling: false,
};

const patternMatchingUpchargesInitial: PricingSimpleItem[] = [
  { id: 'match_repeat_at_seams_edges', name: 'Match Repeat at Seams/Edges', price: '' },
  { id: 'pleat_to_pattern', name: 'Pleat to Pattern', price: '' },
];

const embellishmentsWithPricingInitialState: PricingGroupOrSimpleItem[] = [
  {
    id: 'bandingstraight',
    name: 'Banding - Straight',
    price: '',
  },
  {
    group: {
      name: 'Cording',
      items: [
        {
          id: 'cordingpremade',
          name: 'Pre-Made',
          nameSubText: '(Apply)',
          price: '',
        },
        {
          id: 'cordingwelt',
          name: 'Welt',
          nameSubText: '(Make & Apply)',
          price: '',
        },
      ],
    },
  },
  {
    group: {
      name: 'Flange',
      items: [
        {
          id: 'flangepremade',
          name: 'Pre-Made',
          nameSubText: '(Apply)',
          price: '',
        },
        {
          id: 'flangefromfabric',
          name: 'From Fabric',
          nameSubText: '(Make & Apply)',
          price: '',
        },
      ],
    },
  },
  {
    group: {
      name: 'Trim Pre-made',
      nameSubText: '(Apply)',
      items: [
        {
          id: 'trimpremadewithadhesive',
          name: 'With Adhesive',
          price: '',
        },
        {
          id: 'trimpremademachinestitched',
          name: 'Machine Stitched',
          price: '',
        },
        {
          id: 'trimpremadehandsewn',
          name: 'Hand Sewn',
          price: '',
        },
      ],
    },
  },
  {
    group: {
      name: 'Trim from fabric',
      nameSubText: '(Make & Apply)',
      items: [
        {
          id: 'trimfromfabricwithadhesive',
          name: 'With Adhesive',
          price: '',
        },
        {
          id: 'trimfromfabricmachinestitched',
          name: 'Machine Stitched',
          price: '',
        },
        {
          id: 'trimfromfabrichandsewn',
          name: 'Hand Sewn',
          price: '',
        },
      ],
    },
  },
];

// const descriptionSettings = [
//   { name: 'WO #', isEnabled: false },
//   { name: 'Heading Type (Ripplefold or Pleated)', isEnabled: false },
//   { name: 'Pleat Type (if Pleated)', isEnabled: false },
//   { name: 'Full Length', isEnabled: false, group: true, groupText: 'Drapery Type (if Pleated)' },
//   { name: 'Cafe Curtain', isEnabled: false, group: true, groupText: 'Drapery Type (if Pleated)' },
//   { name: 'Master Carrier (if Ripplefold)', isEnabled: false },
//   { name: 'Mount (if Ripplefold)', isEnabled: false },
//   { name: 'Control Type (if Ripplefold)', isEnabled: false },
//   { name: 'Control Location (if Ripplefold)', isEnabled: false },
//   { name: 'Functionality', isEnabled: false },
//   { name: 'Panel Configuration', isEnabled: false },
//   { name: 'Panel Widths', isEnabled: false },
//   { name: 'Rod Width (if Functional)', isEnabled: false },
//   { name: 'Fullness (if Functional, if Stationary - controlled by Widths per Panel)', isEnabled: false },
//   { name: 'Finished Length (if Ripplefold)', isEnabled: false },
//   { name: 'Return', isEnabled: false },
  
//   { name: 'Display "Yes" when selected', id: 'saleslining:yes', isEnabled: false, group: true, groupText: 'Lining' },
//   { name: 'Display "No" when selected', id: 'saleslining:no', isEnabled: false, group: true, groupText: 'Lining' },
//   { name: 'Show Lining Name', isEnabled: false, group: true, groupText: 'Lining' },
//   { name: 'Lining Purchaser if COM', isEnabled: false, group: true, groupText: 'Lining' },
//   { name: 'Lining Yards if COM', isEnabled: false, group: true, groupText: 'Lining' },
//   { name: 'Lining Yards if Workroom Purchasing', isEnabled: false, group: true, groupText: 'Lining' },
  
//   { name: 'Display "Yes" when selected', id: 'salesinterlining:yes', isEnabled: false, group: true, groupText: 'Interlining' },
//   { name: 'Display "No" when selected', id: 'salesinterlining:no', isEnabled: false, group: true, groupText: 'Interlining' },
//   { name: 'Show Interlining Name', isEnabled: false, group: true, groupText: 'Interlining' },
//   { name: 'Interlining Purchaser if COM', isEnabled: false, group: true, groupText: 'Interlining' },
//   { name: 'Interlining Yards if COM', isEnabled: false, group: true, groupText: 'Interlining' },
//   { name: 'Interlining Yards if Workroom Purchasing', isEnabled: false, group: true, groupText: 'Interlining' },
//   { name: 'Embellishments Selected', isEnabled: false },
//   { name: 'Match Repeat', isEnabled: false },
//   { name: 'Pleat to Pattern (if applied)', isEnabled: false },
//   { name: 'Add-Ons Selected', isEnabled: false },
// ];

// const getDescriptionOptions = (descriptionType: string) => {
//   const modDescriptions = descriptionSettings.map((d, i) => {
//     return {
//       ...d,
//       id: `${descriptionType}${d.name.replace(/\s/g, '').toLowerCase()}`,
//       index: String(i),
//     };
//   });
//   return modDescriptions;
// };

export const draperyItemDetailsFieldsInitialState: DraperyItemDetailsFields = {
  id: 0,
  pricingTypeId: 1,
  pricingTypeName: '',
  isClientPricingActionOn: true,
  clientPricingAction: '',
  valanceAndReturns: [],
  clientPricingValue: '',
  isPartialWidthRoundingOn: true,
  partialWidthRoundUpTypeId: 0,
  partialWidthRoundUpTypeName: '',
  pleatTypesWithPricing: pleatTypesWithPricingInitialState,
  liningAndInterliningWithPricing: liningAndInterliningWithPricingInitialState,
  cafeCurtainsWidthInches: '',
  cafeCurtainsWidthPricing: '',
  extraLargePanelsLengthInches: '',
  extraLargePanelsLengthPricing: '',
  extraLargePanelsWidthInches: '',
  extraLargePanelsWidthPricing: '',
  draperyPinningWidthPricing: '',
  specialityFabricsMarkUpPercentage: '',
  embellishmentsWithPricing: embellishmentsWithPricingInitialState,
  addOnsWithPricing: addOnsWithPricingInitialState,
  salesDescriptionSettings: [],
  orderingDescriptionSettings: [],
  isBundlingSelected: false,
  bundleSelections: bundleSelectionsInitialState,
  patternMatchingUpchargesSelected: false,
  patternMatchingUpcharges: patternMatchingUpchargesInitial,
};
export const draperyItemDetailsFieldsReducer = (
  state: DraperyItemDetailsFields,
  action: DraperyItemDetailsFieldsAction
): DraperyItemDetailsFields => {
  switch (action.type) {
    case 'updateFormFields': {
      return {
        ...state,
        ...action.formFieldUpdates,
      };
    }
    case 'resetFormFields': {
      return draperyItemDetailsFieldsInitialState;
    }
    default: {
      throw new Error('Invalid action type');
    }
  }
};
